import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { duration, easing } from "../../../design-system/tokens/transitions";

/**
 * Figma 2318:116941 specs:
 *  - label: Plus Jakarta Sans Medium 12/18, text/medium-emphasis #5C5468
 *  - label→input gap: 6px
 *  - input box: bg white, 1px solid stroke/200 (#E6E1F3), 8px radius
 *  - input padding: 12px horizontal × 8px vertical (textarea variant)
 *  - input text: Plus Jakarta Sans Medium 12/18, text/high-emphasis #18102B
 *  - required *: #dc2626
 */

export const FormField = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

export const Label = styled.label<{ $required: boolean }>`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  ${({ $required }) =>
    $required &&
    css`
      &::after {
        content: " *";
        color: ${rawColors.accent.red500};
      }
    `}
`;

export const StyledTextarea = styled.textarea<{
  $error: boolean;
  $resize: "none" | "vertical" | "horizontal" | "both";
  $hasFooterActions: boolean;
}>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: ${spacing[2]} ${spacing[3]};
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
  background-color: ${rawColors.structural.white};
  border: 1px solid
    ${({ $error }) => ($error ? rawColors.accent.red500 : rawColors.stroke[200])};
  border-radius: ${borderRadius.lg};
  outline: none;
  resize: ${({ $resize }) => $resize};
  /* Extra bottom padding so text never sits under the footer action buttons. */
  padding-bottom: ${({ $hasFooterActions }) =>
    $hasFooterActions ? "32px" : spacing[2]};
  transition: border-color ${duration.fast} ${easing.smooth}, box-shadow ${duration.fast} ${easing.smooth};

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }

  &:hover:not(:disabled) {
    border-color: ${({ $error }) =>
      $error ? rawColors.accent.red500 : rawColors.stroke[300]};
  }

  &:focus-visible {
    border-color: ${({ $error }) =>
      $error ? rawColors.accent.red500 : rawColors.primary[400]};
    box-shadow: 0 0 0 3px ${rawColors.primary[100]};
  }

  &:disabled {
    background-color: ${rawColors.grey[50]};
    color: ${rawColors.text.lowEmphasis};
    cursor: not-allowed;
  }
`;

/** Relative wrapper needed to absolutely-position `FooterActions`. */
export const TextareaWrap = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * Action buttons pinned to the bottom-right inside the textarea box.
 * The textarea itself adds extra bottom padding when this slot is used.
 */
export const FooterActions = styled.div`
  position: absolute;
  bottom: ${spacing[2]};
  right: ${spacing[3]};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[3]};
  pointer-events: none; /* row itself is transparent; children opt back in */
`;

export const FooterActionButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  margin: 0;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  color: ${rawColors.text.lowEmphasis};
  transition: color 0.15s ease-in-out;

  &:hover {
    color: ${rawColors.text.interactive};
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.text.interactive};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const HelperText = styled.span<{ $error: boolean }>`
  ${typographyMixin("caption", 4)}
  color: ${({ $error }) =>
    $error ? rawColors.accent.red500 : rawColors.text.mediumEmphasis};
`;
